import React, { memo, useMemo } from 'react';
import { connect } from 'react-redux';
import { Menus, Icon, useTheme } from '@Sage/hoi-poi-ui';
import {
    ACTIVITIES,
    AGENDA,
    CONTACTS,
    COMPANIES,
    OPPORTUNITIES,
    SALESORDERS,
} from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { canCreateEntityWithPermissions } from 'utils/entityPermissions';
import useCrudModal from 'containers/components/EntityCrud/hooks/useCrudModal';
import useEmailEditor from 'containers/components/EmailEditor/hooks/useEmailEditor';

import './styles.scss';

const mapStateToProps = (state) => {
    const permissions = state.config.permission;
    return {
        permissions,
        isKpisPremium: permissions?.kpisPremium || false,
        hasWhatsAppCrud: permissions?.whatsappFeature || false,
    };
};

const CreateGlobal = memo(({ permissions, isKpisPremium, hasWhatsAppCrud }) => {
    const {
        crudAccount,
        crudContact,
        crudActivity,
        crudActivityWhatsApp,
        crudEvent,
        crudTask,
        crudOpportunity,
        crudSalesOrder,
        crudGoal,
    } = useCrudModal();
    const { canUseEmail, showEditor } = useEmailEditor();
    const theme = useTheme();

    const items = useMemo(() => {
        let activeItems = [];

        if (canCreateEntityWithPermissions(permissions, COMPANIES)) {
            activeItems.push({
                title: getLiteral('label_account'),
                icon: <Icon name="accounts" color="currentColor" />,
                onClick: crudAccount,
            });
        }

        if (canCreateEntityWithPermissions(permissions, CONTACTS)) {
            activeItems.push({
                title: getLiteral('label_contact'),
                icon: <Icon name="contacts" color="currentColor" />,
                onClick: crudContact,
            });
        }

        if (canCreateEntityWithPermissions(permissions, ACTIVITIES)) {
            activeItems.push({
                title: getLiteral('label_activity'),
                icon: <Icon name="activities" color="currentColor" />,
                onClick: crudActivity,
            });

            if (hasWhatsAppCrud) {
                activeItems.push({
                    title: getLiteral('title_whatsapp_activity'),
                    icon: <Icon name="whatsappGreen" />,
                    onClick: crudActivityWhatsApp,
                });
            }
        }

        if (canCreateEntityWithPermissions(permissions, AGENDA)) {
            activeItems.push({
                title: getLiteral('label_event'),
                icon: <Icon name="calendar" color="currentColor" />,
                onClick: crudEvent,
            });
            activeItems.push({
                title: getLiteral('label_task'),
                icon: <Icon name="tasks" color="currentColor" />,
                onClick: crudTask,
            });
        }

        if (canCreateEntityWithPermissions(permissions, OPPORTUNITIES)) {
            activeItems.push({
                title: getLiteral('title_opportunity'),
                icon: <Icon name="opportunities" color="currentColor" />,
                onClick: crudOpportunity,
            });
        }

        if (canCreateEntityWithPermissions(permissions, SALESORDERS)) {
            activeItems.push({
                title: getLiteral('title_salesorders'),
                icon: <Icon name="salesOrders" color="currentColor" />,
                onClick: crudSalesOrder,
            });
        }

        if (isKpisPremium) {
            activeItems.push({
                title: getLiteral('label_goal'),
                icon: <Icon name="goals" color="currentColor" />,
                onClick: crudGoal,
            });
        }

        if (canUseEmail) {
            activeItems.push({
                title: getLiteral('placeholder_send_email'),
                icon: <Icon name="email" color="currentColor" />,
                onClick: () => showEditor({ entityForTracking: 'emails' }),
            });
        }

        return activeItems;
    }, [
        permissions,
        isKpisPremium,
        hasWhatsAppCrud,
        canUseEmail,
        crudAccount,
        crudContact,
        crudActivity,
        crudActivityWhatsApp,
        crudEvent,
        crudTask,
        crudOpportunity,
        crudSalesOrder,
        crudGoal,
        showEditor,
    ]);

    if (!items.length) return null;

    return (
        <div className="fm-create-global-container">
            <Menus
                className="fm-create-global"
                classNameMenu="fm-create-global-submenu"
                icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none">
                        <circle cx="20" cy="20" r="12" fill={theme.colors.actionMajor[500]} />
                        <path
                            fill={theme.colors.primary.white}
                            fillRule="evenodd"
                            d="M18.8 21.2V26h2.462v-4.8H26v-2.462h-4.738V14H18.8v4.738H14V21.2h4.8Z"
                            clipRule="evenodd"
                        />
                    </svg>
                }
                items={items}
                size="medium"
            />
        </div>
    );
});

export default connect(mapStateToProps)(CreateGlobal);
